import { supabase } from './supabase';

export const PUBLIC_PROFILE_SELECT = [
  'id',
  'name',
  'avatar_url',
  'bio',
  'rating',
  'created_at',
  'company_name',
  'website',
  'banner_url',
  'location',
  'socials',
  'username',
  'full_name',
  'is_public',
  'is_indexable',
  'seo_title',
  'seo_description',
  'seo_image_url',
  'business_name',
].join(',');

// This list is deliberately explicit so the temporary pre-migration fallback
// cannot silently grow when a new profiles column is added. Once the migration
// is deployed, callers always use the owner-bound RPC instead.
const LEGACY_PRIVATE_PROFILE_SELECT = [
  'id',
  'first_name',
  'last_name',
  'name',
  'avatar_url',
  'bio',
  'country',
  'phone_number',
  'account_type',
  'onboarding_completed',
  'rating',
  'created_at',
  'company_name',
  'website',
  'banner_url',
  'location',
  'socials',
  'username',
  'full_name',
  'theme',
  'email_notifications',
  'push_notifications',
  'referral_code',
  'referred_by',
  'seller_terms_accepted_at',
  'seller_onboarded',
  'payout_schedule',
  'payout_schedule_configured',
  'kyc_status',
  'kyc_session_id',
  'wersee_points',
  'stripe_account_id',
  'selling_banned_until',
  'join_source',
  'last_workspace_page',
  'last_active_tab',
  'ai_plan',
  'ai_custom_limit',
  'managed_payments_enabled',
  'workspace_intro_completed',
  'workspace_intro_completed_at',
  'is_next_gen',
  'parent_id',
  'last_seen',
  'storage_welcome_accepted',
  'storage_welcome_accepted_at',
  'managed_payments_enabled_at',
  'stripe_onboarding_complete',
  'stripe_live_pk',
  'is_public',
  'is_indexable',
  'seo_title',
  'seo_description',
  'seo_image_url',
  'business_name',
].join(',');

const LEGACY_PUBLIC_CHECKOUT_PROFILE_SELECT = [
  'id',
  'business_name',
  'avatar_url',
  'stripe_account_id',
].join(',');

export type PrivateProfile = Record<string, unknown> & {
  id: string;
  avatar_url?: string | null;
  full_name?: string | null;
  name?: string | null;
  username?: string | null;
  website?: string | null;
  bio?: string | null;
  theme?: string | null;
  email_notifications?: boolean | null;
  push_notifications?: boolean | null;
  stripe_account_id?: string | null;
  wersee_points?: number | null;
  kyc_status?: string | null;
  last_active_tab?: string | null;
  last_workspace_page?: string | null;
  onboarding_completed?: boolean | null;
  payout_schedule?: string | null;
  seller_terms_accepted_at?: string | null;
  stripe_onboarding_complete?: boolean | null;
  account_type?: string | null;
  seller_onboarded?: boolean | null;
  plan_tier?: string | null;
};

export type PublicCheckoutProfile = {
  seller_id: string;
  business_name: string | null;
  avatar_url: string | null;
  stripe_account_id: string | null;
  sandbox_mode: boolean;
};

const isMissingRpc = (error: unknown, functionName: string) => {
  const candidate = error as { code?: unknown; message?: unknown; details?: unknown } | null;
  const code = String(candidate?.code || '');
  const message = `${String(candidate?.message || '')} ${String(candidate?.details || '')}`.toLowerCase();
  const normalizedFunctionName = functionName.toLowerCase();

  return (
    ['42883', 'PGRST202'].includes(code)
    || (message.includes(normalizedFunctionName) && message.includes('schema cache'))
    || (message.includes(normalizedFunctionName) && message.includes('does not exist'))
  );
};

const isMissingPrivateProfileRpc = (error: unknown) => isMissingRpc(error, 'get_my_private_profile');
const isMissingPublicCheckoutProfileRpc = (error: unknown) => isMissingRpc(
  error,
  'get_public_checkout_profile',
);

const readJwtPayload = (accessToken: string): Record<string, unknown> | null => {
  try {
    const encodedPayload = accessToken.split('.')[1];
    if (!encodedPayload) return null;
    const normalized = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padding = '='.repeat((4 - normalized.length % 4) % 4);
    return JSON.parse(globalThis.atob(`${normalized}${padding}`)) as Record<string, unknown>;
  } catch {
    return null;
  }
};

type LegacyCheckoutProfileRow = {
  id: string;
  business_name: string | null;
  avatar_url: string | null;
  stripe_account_id: string | null;
};

const liveStripeAccount = (value: unknown) => {
  const accountId = typeof value === 'string' ? value.trim() : '';
  return accountId && accountId !== 'sandbox' ? accountId : null;
};

/**
 * Compatibility path used only before Phase A creates the resource-bound RPC.
 * Every lookup stays bound to the supplied checkout resource. Once the RPC is
 * present, this code is never reached. Phase B deliberately makes the private
 * profiles projection unavailable to this fallback.
 */
const getLegacyPublicCheckoutProfile = async (
  resourceType: 'invoice' | 'subscription' | 'quick_pay',
  resourceId: string,
): Promise<PublicCheckoutProfile | null> => {
  let sellerId: string | null = null;
  let checkoutAccountId: string | null = null;
  let resourceSandboxMode: boolean | null = null;

  if (resourceType === 'invoice') {
    const result = await supabase
      .from('invoices')
      .select('user_id,status')
      .eq('id', resourceId)
      .maybeSingle();
    if (result.error) throw result.error;

    const invoice = result.data as { user_id?: string | null; status?: string | null } | null;
    if (!invoice || ['cancelled', 'void'].includes(String(invoice.status || '').toLowerCase())) return null;
    sellerId = invoice.user_id || null;
  } else if (resourceType === 'subscription') {
    const result = await supabase
      .from('subscriptions')
      .select('seller_id,active')
      .eq('id', resourceId)
      .eq('active', true)
      .maybeSingle();
    if (result.error) throw result.error;

    const subscription = result.data as { seller_id?: string | null; active?: boolean | null } | null;
    if (!subscription?.active) return null;
    sellerId = subscription.seller_id || null;
  } else {
    const result = await supabase
      .from('quick_pay_links')
      .select('user_id,stripe_account_id,environment,active')
      .eq('id', resourceId)
      .eq('active', true)
      .maybeSingle();
    if (result.error) throw result.error;

    const paymentLink = result.data as {
      user_id?: string | null;
      stripe_account_id?: string | null;
      environment?: string | null;
      active?: boolean | null;
    } | null;
    if (!paymentLink?.active) return null;
    sellerId = paymentLink.user_id || null;
    checkoutAccountId = liveStripeAccount(paymentLink.stripe_account_id);
    resourceSandboxMode = String(paymentLink.environment || '').toLowerCase() === 'test'
      || paymentLink.stripe_account_id === 'sandbox';
  }

  if (!sellerId) return null;

  const [profileResult, settingsResult] = await Promise.all([
    supabase
      .from('profiles')
      .select(LEGACY_PUBLIC_CHECKOUT_PROFILE_SELECT)
      .eq('id', sellerId)
      .maybeSingle(),
    supabase
      .from('business_settings')
      .select('sandbox_mode')
      .eq('user_id', sellerId)
      .maybeSingle(),
  ]);
  if (profileResult.error) throw profileResult.error;

  const profile = profileResult.data as LegacyCheckoutProfileRow | null;
  if (!profile) return null;

  const profileAccountId = liveStripeAccount(profile.stripe_account_id);
  const resolvedAccountId = checkoutAccountId || profileAccountId;
  const settings = settingsResult.data as { sandbox_mode?: boolean | null } | null;
  // Anonymous callers cannot normally read another seller's business settings.
  // If that state is unavailable, fail closed instead of accidentally turning
  // a seller's sandbox checkout into a live connected-account payment.
  const settingsSandboxMode = Boolean(settingsResult.error)
    || !settings
    || Boolean(settings.sandbox_mode);
  const sandboxMode = resourceSandboxMode ?? (!resolvedAccountId || settingsSandboxMode);

  return {
    seller_id: profile.id,
    business_name: profile.business_name ?? null,
    avatar_url: profile.avatar_url ?? null,
    stripe_account_id: resourceSandboxMode === true ? null : resolvedAccountId,
    sandbox_mode: sandboxMode,
  };
};

/**
 * Reads the complete profile for the currently authenticated first-party user.
 *
 * The RPC accepts no user id and enforces auth.uid() plus the OAuth client_id
 * boundary in Postgres. A direct table read exists only as a narrow rollout
 * bridge while the RPC migration has not been applied; it fails closed for
 * OAuth-client or unparseable session tokens.
 */
export const getMyPrivateProfile = async <T extends PrivateProfile = PrivateProfile>(): Promise<T | null> => {
  const rpcResult = await supabase.rpc('get_my_private_profile');
  if (!rpcResult.error) return (rpcResult.data as T | null) ?? null;
  if (!isMissingPrivateProfileRpc(rpcResult.error)) throw rpcResult.error;

  const [{ data: userResult, error: userError }, { data: sessionResult, error: sessionError }] = await Promise.all([
    supabase.auth.getUser(),
    supabase.auth.getSession(),
  ]);
  if (userError) throw userError;
  if (sessionError) throw sessionError;
  if (!userResult.user || !sessionResult.session) {
    throw new Error('A signed-in Wersee session is required to read your private profile.');
  }

  const claims = readJwtPayload(sessionResult.session.access_token);
  if (!claims || (typeof claims.client_id === 'string' && claims.client_id.trim())) {
    throw new Error('OAuth client sessions cannot use the private-profile rollout fallback.');
  }

  console.warn(
    'Private-profile RPC is not deployed yet; using the owner-only pre-migration compatibility read.',
  );

  const legacyResult = await supabase
    .from('profiles')
    .select(LEGACY_PRIVATE_PROFILE_SELECT)
    .eq('id', userResult.user.id)
    .maybeSingle();
  if (legacyResult.error) throw legacyResult.error;

  return (legacyResult.data as T | null) ?? null;
};

export const getPublicCheckoutProfile = async (
  resourceType: 'invoice' | 'subscription' | 'quick_pay',
  resourceId: string,
): Promise<PublicCheckoutProfile | null> => {
  const { data, error } = await supabase.rpc('get_public_checkout_profile', {
    p_resource_type: resourceType,
    p_resource_id: resourceId,
  });
  if (error) {
    if (!isMissingPublicCheckoutProfileRpc(error)) throw error;

    console.warn(
      'Public-checkout profile RPC is not deployed yet; using the resource-bound Phase A compatibility read.',
    );
    return getLegacyPublicCheckoutProfile(resourceType, resourceId);
  }

  const row = Array.isArray(data) ? data[0] : data;
  return (row as PublicCheckoutProfile | null) ?? null;
};
